<script setup lang="ts">
import { onBeforeMount, provide, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getHostRouter } from './mfe.config';
import { useAuthFlowStore } from '@Stores/authFlow.store';
import { useAuthSessionStore } from '@Stores/authSession.store';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import { verifyActivitySession, getCompanySlug } from 'host/mfeBridge';
import { verifyDeviceApi } from '@Api/deviceAuth.api';
import { handleStatusCode } from '@Utils/statusCodeHandler';
import { resolveDeviceAuthEnabled } from '@Use/useDeviceAuthFlag';
import { getDashboardContextApi } from '@Api/navigation.api';


// state
const isReady = ref(false);

// Grab internal MFE router
const router = useRouter();
const authStore = useAuthFlowStore();
const authSessionStore = useAuthSessionStore();
const deviceAuthStore = useDeviceAuthStore();
const { advanceFlow, advanceFlowReplace } = useFlowNavigation();

// The shared layouts render <back-to-dashboard/>, which stays hidden until these are provided.
provide('backToDashboard:hostRouter', getHostRouter());
provide('backToDashboard:slug', () => getCompanySlug());
provide('backToDashboard:flowStore', authStore);
provide('backToDashboard:getDashboardContext', getDashboardContextApi);


const bootstrapAuthFlow = async (shouldAdvance: boolean) => {
    const authFlow = router.currentRoute.value.query?.flow;

    if (typeof authFlow !== 'string') {
        console.error('[Auth MFE] Missing or invalid authFlow query param');
        return;
    }

    await verifyActivitySession();
    await authStore.loadAuthFlow(authFlow);

    if (shouldAdvance) {
        await advanceFlowReplace();
    }
};

const bootstrapDeviceAuth = async () => {
    deviceAuthStore.setVerifying(true);

    try {
        const response = await verifyDeviceApi();
        deviceAuthStore.handleVerifyDeviceResponse(response);
        await handleStatusCode(response, router, advanceFlow);
    } catch (err) {
        console.error('[Auth MFE] Device verification failed:', err);
        deviceAuthStore.setError('Unable to verify device. Please try again.');
        router.replace({ name: 'verify-device' });
    } finally {
        deviceAuthStore.setVerifying(false);
    }
};

const TERMINAL_ROUTES = new Set(['idv-permission-denied', 'idv-ended']);

onBeforeMount(async () => {
    // Reset fullLoginInitiated so it does not leak into a later non-4006 step-up MFA flow
    authSessionStore.reset();

    const currentRouteName = router.currentRoute.value.name as string;
    if (TERMINAL_ROUTES.has(currentRouteName)) {
        isReady.value = true;
        return;
    }

    const hostRouter = getHostRouter();
    const hostQuery = hostRouter?.currentRoute?.value?.query ?? {};
    const temporarySessionId = hostQuery.temporarySessionId as string | undefined;

    if (temporarySessionId) {
        // Mobile IDV flow: skip device-auth but still load the auth flow
        await bootstrapAuthFlow(true);
        isReady.value = true;
        return;
    }

    const deviceAuthEnabled = await resolveDeviceAuthEnabled();
    const useLegacyAuth = !deviceAuthEnabled;

    if (!useLegacyAuth) {
        await bootstrapDeviceAuth();
        isReady.value = true;
        return;
    }

    const shouldBootstrap: boolean = router.currentRoute.value.name === 'bootstrap';

    if (shouldBootstrap) {
        await router.replace({
            name: 'bootstrap',
            query: hostQuery,
            replace: true
        });
    }

    await bootstrapAuthFlow(shouldBootstrap);
    isReady.value = true;
});


</script>

<template>
    <app-page-loader v-if="!isReady" />
    <div v-else class="auth-container">
        <router-view />
    </div>
</template>


<style lang="scss" scoped>

.auth-container {
    height: 100%;

    & > * {
        height: 100%;
    }
}

</style>
